<template>
  <div class="vis-hash-check">
    <div v-if="showForm">
      <div>
        <p>Please export a file from your forensic software and upload it here.</p>
        <p>We will check whether a victim impact statement is available for any of the images.</p>
        <p class="u-mb-0">Supported file formats:</p>
        <ul>
          <li>LACE PhotoDNA export (XML)</li>
          <li>ProjectVIC format (JSON)</li>
          <li>LACE Excel spreadsheet (XLSX)</li>
          <li>Semantics 21 LASERi-X (CSV)</li>
          <li>Comma-separated values (CSV) - Please use column headers "hash", "md5", "sha1", "pdna", or "photodna"</li>
        </ul>
      </div>
      <form
        method="POST"
        action=""
        class="vis-form"
        @submit.prevent="submitVisForm"
      >
        <div class="form-title">Forensic Import</div>
        <div
          v-if="formErrors.__all__"
          class="form-error alert alert--error"
        >
          <span v-for="error in formErrors.__all__">
            {{ error }}
          </span>
        </div>
        <div class="vis-field-div">
          <label for="export_file">Select forensic export file <small class="required">(required)</small>:</label>
          <div class="input-error-group">
            <input type="file" name="export_file" id="export_file" required>
            <div
              v-if="formErrors.export_file"
              class="form-error alert alert--error"
            >
              <span v-for="error in formErrors.export_file">
                {{ error }}
              </span>
            </div>
          </div>
        </div>
        <div class="vis-field-div">
          <label for="case_number">Case number or reference number <small class="required">(required)</small>:</label>
          <div class="input-error-group">
            <input type="text" name="case_number" id="case_number" required>
            <div
              v-if="formErrors.case_number"
              class="form-error alert alert--error"
            >
              <span v-for="error in formErrors.case_number">
                {{ error }}
              </span>
            </div>
          </div>
        </div>
        <div class="vis-field-div">
          <label for="suspect_name" title="The name is used only to keep track of the request in our system">
            Suspect's Name <small class="required">(required)</small>:
          </label>
          <div class="input-error-group">
            <input type="text" id="suspect_name" name="suspect_name" required>
            <div
              v-if="formErrors.suspect_name"
              class="form-error alert alert--error"
            >
              <span v-for="error in formErrors.suspect_name">
                {{ error }}
              </span>
            </div>
          </div>
        </div>

        <div class="vis-field-div">
          <label for="crown_name">Crown's Name <small>(if known)</small>:</label>
          <div class="input-error-group">
            <input type="text" id="crown_name" name="crown_name">
            <div
              v-if="formErrors.crown_name"
              class="form-error alert alert--error"
            >
              <span v-for="error in formErrors.crown_name">
                {{ error }}
              </span>
            </div>
          </div>
        </div>

        <div class="vis-field-div">
          <label for="crown_email">Crown's Email <small>(if known)</small>:</label>
          <div class="input-error-group">
            <input type="email" id="crown_email" name="crown_email">
            <div
              v-if="formErrors.crown_email"
              class="form-error alert alert--error"
            >
              <span v-for="error in formErrors.crown_email">
                {{ error }}
              </span>
            </div>
          </div>
        </div>

        <div class="vis-field-div">
          <label for="court_stage">Court Stage <small class="required">(required)</small>:</label>
          <div class="input-error-group">
            <select id="court_stage" name="court_stage" required>
              <option value=""> </option>
              <option
                  v-for="(courtStage, key) in courtStages"
                  :value="key"
                  :key="key"
              >
                {{ courtStage }}
              </option>
            </select>
            <div
              v-if="formErrors.court_stage"
              class="form-error alert alert--error"
            >
              <span v-for="error in formErrors.court_stage">
                {{ error }}
              </span>
            </div>
          </div>
        </div>

        <div class="vis-field-div">
          <label for="police_force_name">
            Police force <small class="required">(required)</small>:
          </label>
          <div class="input-error-group">
            <input type="text" id="police_force_name" name="police_force_name" required>
            <div
              v-if="formErrors.police_force_name"
              class="form-error alert alert--error"
            >
              <span v-for="error in formErrors.police_force_name">
                {{ error }}
              </span>
            </div>
          </div>
        </div>

        <div class="vis-field-div">
          <label for="province_of_jurisdiction">
            Province of Jurisdiction <small class="required">(required)</small>:
          </label>
          <div class="input-error-group">
            <select id="province_of_jurisdiction" name="province_of_jurisdiction" required>
              <option value=""> </option>
              <option
                  v-for="(province, key) in provinces"
                  :value="key"
                  :key="key"
              >
                {{ province }}
              </option>
            </select>
            <div
              v-if="formErrors.province_of_jurisdiction"
              class="form-error alert alert--error"
            >
              <span v-for="error in formErrors.province_of_jurisdiction">
                {{ error }}
              </span>
            </div>
          </div>
        </div>

        <label for="notes">Comments <small>(optional)</small>:</label>
        <div class="input-error-group">
          <textarea name="vis_request_text" cols="30" rows="5"></textarea>
          <div
              v-if="formErrors.vis_request_text"
              class="form-error alert alert--error"
            >
              <span v-for="error in formErrors.vis_request_text">
                {{ error }}
              </span>
            </div>
        </div>

        <button
          class="btn btn--primary btn--inline"
          type="submit"
        >
          Submit and Upload
        </button>
      </form>
    </div>
    <div
      v-else
      class="vis-hash-success"
    >
        <div
          class="alert alert--info"
          v-for="warning in responseWarnings"
        >
          {{ warning }}
        </div>
        Your VIS check and request has been submitted successfully.
        We will contact you within 3 business days with results.
        <a
          class="btn btn--primary"
          href=""
        >
          Submit Another
        </a>
    </div>
    <div class="u-fontsize-sm legal-footer">
      The VIS Hash Check Tool is a creation of, and is owned by, the Canadian Centre for Child Protection
      Inc. (C3P) and is intended solely for police use.
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Cookies from 'js-cookie'


export default {
  name: 'VisHashCheckTool',
  props: {
    courtStages: {
      type: Object,
      default: () => {}
    },
    provinces: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      formErrors: {},
      responseWarnings: [],
      showForm: true,
    }
  },
  mounted() {
    this.clearFormErrors()
  },
  methods: {
    clearFormErrors() {
      this.formErrors = {
        __all__: null,
        export_file: null,
        case_number: null,
        suspect_name: null,
        crown_name: null,
        crown_email: null,
        court_stage: null,
        police_force_name: null,
        province_of_jurisdiction: null,
        vis_request_text: null,
      }
    },
    submitVisForm(event) {
      let formData = new FormData(event.target)
      const csrftoken = Cookies.get('csrftoken')
      this.clearFormErrors()
      axios.post(
        '/en/forensic/upload/',
        formData,
        {'headers': {'X-CSRFToken': csrftoken, 'Content-Type': 'multipart/form-data'}},
      ).then((response) => {
        if (response.status === 200) {
          this.showForm = false
          if (response.data.warnings) {
            this.responseWarnings = response.data.warnings
          }
        }
      }).catch((error) => {
        this.formErrors = error.response.data
        this.$nextTick(() => {
          document.querySelector('.form-error')?.scrollIntoView()
        })
      })
    }
  }
}
</script>

<style scoped>
.legal-footer {
  margin-top: 10%;
  font-style: italic;
}
.vis-form {
  border: 2px solid lightgray;
  padding: 15px;
  display: flex;
  flex-direction: column;
  justify-content: stretch;
  gap: 1.25rem;
  max-width: 80%;
}
.form-title {
  font-weight: bold;
  font-size: 1.4em;
}
.vis-field-div {
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 1.25rem;
  label {
    margin: 0;
    display: flex;
    align-items: baseline;
    gap: 0.2rem;
  }
}
.vis-hash-success {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  a {
    width: 25%;
  }
}
small.required {
  color: #a42334;
}
textarea {
  resize: none;
}
.form-error {
  padding: 0.5rem;
}
.input-error-group {
  width: 100%;
}
</style>